import {randomInt} from "../Utility/utilities";
import {TechnologyDefinition, getTechnologyScienceWeight} from "../State/TechnologyData";

export type Coord = { row: number; col: number };
export type WirePair = { id: string; color: string; start: Coord; end: Coord; basePath: Coord[] };
export type PuzzleDefinition = { size: number; obstacles: string[]; pairs: WirePair[]; paintLimit: number };
export type Move = { key: string; previous?: string; next?: string };
export type PuzzleState = { colors: Record<string, string | undefined>; activePair: string; history: Move[]; future: Move[] };

export const STORAGE_PREFIX = "science-puzzle-";
export const COLOR_POOL = ["#0ea5e9", "#f97316", "#10b981", "#ec4899", "#8b5cf6", "#f59e0b", "#06b6d4", "#ef4444", "#22c55e", "#475569", "#14b8a6", "#d946ef"];

export const keyFor = (coord: Coord) => `${coord.row}-${coord.col}`;

export const neighbors = (coord: Coord, size: number) => [
    {row: coord.row - 1, col: coord.col},
    {row: coord.row + 1, col: coord.col},
    {row: coord.row, col: coord.col - 1},
    {row: coord.row, col: coord.col + 1},
].filter(n => n.row >= 0 && n.col >= 0 && n.row < size && n.col < size);

const findShortestPath = (start: Coord, end: Coord, blocked: Set<string>, size: number): Coord[] | null => {
    const queue: {coord: Coord; path: Coord[]}[] = [{coord: start, path: [start]}];
    const visited = new Set<string>([keyFor(start)]);
    while (queue.length) {
        const current = queue.shift();
        if (!current) break;
        if (current.coord.row === end.row && current.coord.col === end.col) return current.path;
        for (const next of neighbors(current.coord, size)) {
            const key = keyFor(next);
            if (visited.has(key) || blocked.has(key)) continue;
            visited.add(key);
            queue.push({coord: next, path: [...current.path, next]});
        }
    }
    return null;
};

export function buildPuzzleDefinition(technology: TechnologyDefinition): PuzzleDefinition {
    const scienceWeight = Math.max(technology.cost.length, getTechnologyScienceWeight(technology));
    const difficulty = Math.min(1, scienceWeight / 120);
    const size = Math.min(25, Math.max(5, Math.round(5 + difficulty * 18 + randomInt(0, 2))));
    const pairCount = Math.min(12, Math.max(1, Math.round(1 + difficulty * 8)));
    const obstacleCount = Math.min(size * size / 3, Math.round(5 + difficulty * 18));
    const attempts = 80;

    for (let attempt = 0; attempt < attempts; attempt++) {
        const obstacles = new Set<string>();
        while (obstacles.size < obstacleCount) {
            const coord: Coord = {row: randomInt(0, size - 1), col: randomInt(0, size - 1)};
            obstacles.add(keyFor(coord));
        }

        const pairs: WirePair[] = [];
        const occupied = new Set<string>(obstacles);
        for (let i = 0; i < pairCount; i++) {
            let tries = 0;
            let start: Coord | null = null;
            let end: Coord | null = null;
            while (tries < 60 && (!start || !end)) {
                tries++;
                const candidateStart: Coord = {row: randomInt(0, size - 1), col: randomInt(0, size - 1)};
                const candidateEnd: Coord = {row: randomInt(0, size - 1), col: randomInt(0, size - 1)};
                if (keyFor(candidateStart) === keyFor(candidateEnd)) continue;
                if (occupied.has(keyFor(candidateStart)) || occupied.has(keyFor(candidateEnd))) continue;
                start = candidateStart;
                end = candidateEnd;
            }
            if (!start || !end) break;

            const path = findShortestPath(start, end, occupied, size);
            if (!path) break;
            const color = COLOR_POOL[i % COLOR_POOL.length];
            path.forEach(step => occupied.add(keyFor(step)));
            pairs.push({id: `pair-${i}`, color, start, end, basePath: path});
            occupied.add(keyFor(start));
            occupied.add(keyFor(end));
        }

        if (pairs.length === pairCount) {
            const shortest = pairs.reduce((sum, pair) => sum + pair.basePath.length, 0);
            const slackRatio = Math.max(0.05, 0.4 - difficulty * 0.3);
            const paintLimit = Math.round(shortest * (1 + slackRatio));
            return {size, obstacles: Array.from(obstacles), pairs, paintLimit};
        }
    }

    throw new Error("Failed to generate puzzle");
}

const definitionKey = (definition: PuzzleDefinition) => `${definition.size}-${definition.obstacles.join(";")}-${definition.pairs.length}`;

export function loadPuzzleState(definition: PuzzleDefinition, techId: string): PuzzleState {
    const stored = localStorage.getItem(`${STORAGE_PREFIX}${techId}`);
    if (stored) {
        try {
            const parsed = JSON.parse(stored);
            if (parsed?.definitionKey === definitionKey(definition)) {
                return {
                    colors: parsed.colors ?? {},
                    activePair: parsed.activePair ?? definition.pairs[0].id,
                    history: parsed.history ?? [],
                    future: [],
                };
            }
        } catch (error) {
            console.warn("Failed to restore science puzzle", error);
        }
    }
    return {colors: {}, activePair: definition.pairs[0].id, history: [], future: []};
}

export function serialize(definition: PuzzleDefinition, state: PuzzleState, techId: string) {
    localStorage.setItem(`${STORAGE_PREFIX}${techId}`, JSON.stringify({
        definitionKey: definitionKey(definition),
        colors: state.colors,
        activePair: state.activePair,
        history: state.history,
    }));
}

const clampToLimit = (definition: PuzzleDefinition, colors: Record<string, string | undefined>) => {
    const entries = Object.entries(colors);
    if (entries.length <= definition.paintLimit) return colors;
    const trimmed: Record<string, string> = {};
    entries.slice(0, definition.paintLimit).forEach(([key, value]) => { if (value) trimmed[key] = value; });
    return trimmed;
};

export const isEndpoint = (pair: WirePair, key: string) => key === keyFor(pair.start) || key === keyFor(pair.end);

export const hasPath = (definition: PuzzleDefinition, colors: Record<string, string | undefined>, pair: WirePair) => {
    const allowed = new Set<string>();
    const targetColor = pair.color;
    Object.entries(colors).forEach(([key, value]) => { if (value === targetColor) allowed.add(key); });
    allowed.add(keyFor(pair.start));
    allowed.add(keyFor(pair.end));

    const blocked = new Set(definition.obstacles);
    const queue: Coord[] = [pair.start];
    const visited = new Set<string>([keyFor(pair.start)]);
    while (queue.length) {
        const current = queue.shift()!;
        if (current.row === pair.end.row && current.col === pair.end.col) return true;
        for (const neighbor of neighbors(current, definition.size)) {
            const key = keyFor(neighbor);
            if (visited.has(key) || blocked.has(key) || !allowed.has(key)) continue;
            visited.add(key);
            queue.push(neighbor);
        }
    }
    return false;
};

export const getPaintUsed = (state: PuzzleState) => Object.keys(state.colors).length;

export const setColorForKey = (
    definition: PuzzleDefinition,
    state: PuzzleState,
    key: string,
    color?: string,
): PuzzleState => {
    const previous = state.colors[key];
    if (previous === color) return state;
    if (color && Object.keys(state.colors).length >= definition.paintLimit && !state.colors[key]) return state;
    const nextColors = {...state.colors};
    if (color) nextColors[key] = color; else delete nextColors[key];
    return {
        ...state,
        colors: clampToLimit(definition, nextColors),
        history: [...state.history, {key, previous, next: color}],
        future: [],
    };
};
